import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { FiUpload, FiEdit, FiStar, FiSave, FiLoader, FiCpu, FiHardDrive, FiPrinter, FiMonitor, FiX, FiArrowLeft } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ASSET_BASE_URL = (process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || 'https://pcvasquez.com/api')
  : 'http://localhost:5000/api').replace(/\/api$/, '');

const ProductForm = ({ mode = 'create', productId, productData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    category: '',
    brand: '',
    model: '',
    warranty: '12',
    featured: false,
    specifications: {
      processor: '',
      ram: '',
      storage: '',
      graphics: '',
      os: '',
      scannerType: '',
      connectivity: '',
      general: ''
    }
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoadingProduct, setIsLoadingProduct] = useState(mode === 'edit');
  const [touched, setTouched] = useState({});

  const categories = [
    { value: 'laptops', label: 'Laptops' },
    { value: 'desktop', label: 'Computadores de Escritorio' },
    { value: 'componentes', label: 'Componentes' },
    { value: 'perifericos', label: 'Periféricos' },
    { value: 'barcode', label: 'Lectores de Código de Barras' },
    { value: 'servidores', label: 'Servidores' },
    { value: 'redes', label: 'Redes y Conectividad' },
    { value: 'almacenamiento', label: 'Almacenamiento' }
  ];

  const categoryIcons = {
    laptops: <FiCpu className="inline mr-2" />,
    desktop: <FiMonitor className="inline mr-2" />,
    componentes: <FiHardDrive className="inline mr-2" />,
    perifericos: <FiPrinter className="inline mr-2" />,
    barcode: <FiPrinter className="inline mr-2" />,
    servidores: <FiHardDrive className="inline mr-2" />,
    redes: <FiCpu className="inline mr-2" />,
    almacenamiento: <FiHardDrive className="inline mr-2" />
  };

  const brands = [
    'HP', 'Dell', 'Lenovo', 'Asus', 'Acer', 'Apple', 'Microsoft', 
    'Samsung', 'Toshiba', 'MSI', 'Huawei', 'Xiaomi', 'Logitech',
    'Epson', 'Canon', 'Zebra', 'Honeywell', 'Datalogic'
  ];

  const operatingSystems = [
    'Windows 10', 'Windows 11', 'macOS', 'Linux', 'Chrome OS', 'Sin sistema operativo'
  ];

  // Función para comprimir imágenes
  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      // Si el archivo es muy pequeño o no es una imagen, no comprimir
      if (file.size < 500000) { // Menos de 500KB
        resolve(file);
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calcular nuevas dimensiones manteniendo aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convertir a blob con calidad reducida
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Crear nuevo archivo con el blob comprimido
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Error al comprimir la imagen'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Función para validar tamaño de archivo
  const validateFileSize = (file, maxSizeMB = 2) => {
    if (!file.type.startsWith('image/')) {
      toast.error(`"${file.name}" no es una imagen válida.`);
      return false;
    }
    const maxSize = maxSizeMB * 1024 * 1024; // Convertir a bytes
    if (file.size > maxSize) {
      toast.error(`La imagen "${file.name}" es demasiado grande. Máximo permitido: ${maxSizeMB}MB`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        className: "border-l-4 border-red-500 bg-white shadow-lg"
      });
      return false;
    }
    return true;
  };

  // Función para parsear specifications
  const parseSpecifications = (specs) => {
    if (!specs) return {};
    
    if (typeof specs === 'string') {
      try {
        return JSON.parse(specs);
      } catch (error) {
        console.error('Error parsing specifications:', error);
        return {};
      }
    }
    
    return specs || {};
  };

  // Cargar datos del producto cuando productData cambie
  useEffect(() => {
    if (mode === 'edit' && productData) {
      console.log('Cargando datos del producto:', productData);
      
      const product = productData.data || productData;
      const specifications = parseSpecifications(product.specifications);

      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        stock: product.stock || '',
        category: product.category || '',
        brand: product.brand || '',
        model: product.model || '',
        warranty: product.warranty || '12',
        featured: product.featured || false,
        specifications: {
          processor: specifications.processor || '',
          ram: specifications.ram || '',
          storage: specifications.storage || '',
          graphics: specifications.graphics || '',
          os: specifications.os || '',
          scannerType: specifications.scannerType || '',
          connectivity: specifications.connectivity || '',
          general: specifications.general || ''
        }
      });

      if (product.images && product.images.length > 0) {
        setExistingImages(product.images);
      }

      setIsLoadingProduct(false);
    }
  }, [productData, mode]);

  // Si estamos en modo edición y no hay productData, cargar desde la API
  useEffect(() => {
    const loadProductFromAPI = async () => {
      if (mode === 'edit' && productId && !productData) {
        try {
          setIsLoadingProduct(true);
          const response = await api.get(`/products/${productId}`);
          const product = response.data.data || response.data;
          
          const specifications = parseSpecifications(product.specifications);

          setFormData({
            name: product.name || '',
            description: product.description || '',
            price: product.price || '',
            stock: product.stock || '',
            category: product.category || '',
            brand: product.brand || '',
            model: product.model || '',
            warranty: product.warranty || '12',
            featured: product.featured || false,
            specifications: {
              processor: specifications.processor || '',
              ram: specifications.ram || '',
              storage: specifications.storage || '',
              graphics: specifications.graphics || '',
              os: specifications.os || '',
              scannerType: specifications.scannerType || '',
              connectivity: specifications.connectivity || '',
              general: specifications.general || ''
            }
          });

          if (product.images && product.images.length > 0) {
            setExistingImages(product.images);
          }
        } catch (error) {
          console.error('Error cargando producto:', error);
          toast.error('Error al cargar el producto', {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            className: "border-l-4 border-red-500 bg-white shadow-lg"
          });
        } finally {
          setIsLoadingProduct(false);
        }
      }
    };

    loadProductFromAPI();
  }, [mode, productId, productData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setTouched(prev => ({ ...prev, [name]: true }));
    
    if (name.startsWith('specifications.')) {
      const specName = name.split('.')[1];
      setFormData({
        ...formData,
        specifications: {
          ...formData.specifications,
          [specName]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const maxImages = 4 - existingImages.length + imagesToDelete.length;
      const availableSlots = Math.max(0, maxImages - imageFiles.length);
      const selectedFiles = files.slice(0, availableSlots);
      if (files.length > availableSlots) {
        toast.info(`Solo puedes mantener hasta ${maxImages} imágenes para este producto.`);
      }
      
      // Validar tamaño de archivos antes de procesar
      const validFiles = selectedFiles.filter(file => validateFileSize(file, 3)); // 3MB máximo
      
      if (validFiles.length === 0) return;
      
      const newImageFiles = [...imageFiles];
      const newPreviews = [...imagePreviews];
      
      // Mostrar toast de procesamiento
      toast.info('Comprimiendo imágenes...', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
      });
      
      for (const file of validFiles) {
        try {
          // Comprimir imagen
          const compressedFile = await compressImage(file);
          newImageFiles.push(compressedFile);
          
          // Crear vista previa
          const reader = new FileReader();
          reader.onloadend = () => {
            newPreviews.push(reader.result);
            if (newPreviews.length === newImageFiles.length) {
              setImagePreviews([...newPreviews]);
            }
          };
          reader.readAsDataURL(compressedFile);
        } catch (error) {
          console.error('Error comprimiendo imagen:', error);
          // Si falla la compresión, usar el archivo original
          newImageFiles.push(file);
          
          const reader = new FileReader();
          reader.onloadend = () => {
            newPreviews.push(reader.result);
            if (newPreviews.length === newImageFiles.length) {
              setImagePreviews([...newPreviews]);
            }
          };
          reader.readAsDataURL(file);
        }
      }
      
      setImageFiles(newImageFiles);
      e.target.value = '';
    }
  };

  const removeImage = (index, type) => {
    if (type === 'existing') {
      setImagesToDelete([...imagesToDelete, existingImages[index]]);
      const newExisting = [...existingImages];
      newExisting.splice(index, 1);
      setExistingImages(newExisting);
    } else {
      const newFiles = [...imageFiles];
      const newPreviews = [...imagePreviews];
      
      newFiles.splice(index, 1);
      newPreviews.splice(index, 1);
      
      setImageFiles(newFiles);
      setImagePreviews(newPreviews);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Nombre es requerido';
    if (!formData.description.trim()) newErrors.description = 'Descripción es requerida';
    if (!formData.price || isNaN(formData.price) || formData.price <= 0) 
      newErrors.price = 'Precio debe ser mayor a 0';
    if (!formData.stock || isNaN(formData.stock) || formData.stock < 0) 
      newErrors.stock = 'Stock no puede ser negativo';
    if (!formData.category) newErrors.category = 'Categoría es requerida';
    if (!formData.brand) newErrors.brand = 'Marca es requerida';
    if (!formData.model) newErrors.model = 'Modelo es requerido';
    
    if (mode === 'create' && imageFiles.length === 0) {
      newErrors.images = 'Al menos una imagen es requerida';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFieldError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setTouched({
      name: true,
      description: true,
      price: true,
      stock: true,
      category: true,
      brand: true,
      model: true
    });
    
    if (!validateForm()) {
      toast.error('Por favor, complete todos los campos requeridos', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        className: "border-l-4 border-red-500 bg-white shadow-lg"
      });
      return;
    }

    setLoading(true);
    
    try {
      const data = new FormData();
      
      // Agregar datos del formulario
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'specifications') {
          data.append(key, JSON.stringify(value));
        } else {
          data.append(key, value);
        }
      });
      
      // Verificar tamaño total de las imágenes antes de enviar
      let totalSize = 0;
      imageFiles.forEach(file => {
        totalSize += file.size;
      });
      
      // Mostrar advertencia si el tamaño total es muy grande
      if (totalSize > 10 * 1024 * 1024) { // 10MB
        toast.warn('Las imágenes son bastante grandes. El proceso puede tomar unos segundos...', {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: true,
          className: "border-l-4 border-yellow-500 bg-white shadow-lg"
        });
      }
      
      // Agregar todas las imágenes nuevas (ya comprimidas)
      imageFiles.forEach((file) => {
        data.append('images', file);
      });

      // Agregar información de imágenes a eliminar (solo en modo edición)
      if (mode === 'edit' && imagesToDelete.length > 0) {
        data.append('imagesToDelete', JSON.stringify(imagesToDelete));
      }

      // Configurar axios con timeout extendido para archivos grandes
      const axiosConfig = {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000, // 60 segundos timeout
        maxContentLength: 50 * 1024 * 1024, // 50MB máximo
        maxBodyLength: 50 * 1024 * 1024, // 50MB máximo
      };

      let response;
      if (mode === 'edit') {
        response = await api.put(`/products/${productId}`, data, axiosConfig);
        
        toast.success('Producto actualizado exitosamente', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          className: "border-l-4 border-green-500 bg-white shadow-lg"
        });
      } else {
        response = await api.post('/products', data, axiosConfig);
        
        toast.success('Producto creado exitosamente', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          className: "border-l-4 border-green-500 bg-white shadow-lg"
        });
      }

      if (onSubmit) {
        onSubmit(response.data);
      }
    } catch (err) {
      console.error('Error al guardar el producto:', err);
      
      let errorMessage = err.response?.data?.message || 
        (mode === 'edit' ? 'Error al actualizar el producto' : 'Error al crear el producto');
      
      // Mensajes específicos para errores de tamaño
      if (err.code === 'ERR_BAD_REQUEST' && err.response?.status === 413) {
        errorMessage = 'Las imágenes son demasiado grandes. Por favor, reduzca el tamaño o use menos imágenes.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'La operación tardó demasiado tiempo. Por favor, intente con imágenes más pequeñas.';
      }
      
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        className: "border-l-4 border-red-500 bg-white shadow-lg"
      });
    } finally {
      setLoading(false);
    }
  };

  const renderSpecificationFields = () => {
    switch(formData.category) {
      case 'laptops':
      case 'Computadores de escritorio':
      case 'servidores':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0C4B45] mb-1">Procesador</label>
              <input
                type="text"
                name="specifications.processor"
                value={formData.specifications.processor || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
                placeholder="Ej: Intel Core i7-10700K"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0C4B45] mb-1">Memoria RAM</label>
              <input
                type="text"
                name="specifications.ram"
                value={formData.specifications.ram || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
                placeholder="Ej: 16GB DDR4"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0C4B45] mb-1">Almacenamiento</label>
              <input
                type="text"
                name="specifications.storage"
                value={formData.specifications.storage || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
                placeholder="Ej: 512GB SSD + 1TB HDD"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0C4B45] mb-1">Tarjeta Gráfica</label>
              <input
                type="text"
                name="specifications.graphics"
                value={formData.specifications.graphics || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
                placeholder="Ej: NVIDIA GeForce RTX 3060"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#0C4B45] mb-1">Sistema Operativo</label>
              <select
                name="specifications.os"
                value={formData.specifications.os || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
              >
                <option value="">Seleccione un sistema operativo</option>
                {operatingSystems.map(os => (
                  <option key={os} value={os}>{os}</option>
                ))}
              </select>
            </div>
          </div>
        );
      case 'barcode':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0C4B45] mb-1">Tipo de Escáner</label>
              <input
                type="text"
                name="specifications.scannerType"
                value={formData.specifications.scannerType || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
                placeholder="Ej: Láser, CCD, Imager"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0C4B45] mb-1">Conectividad</label>
              <input
                type="text"
                name="specifications.connectivity"
                value={formData.specifications.connectivity || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
                placeholder="Ej: USB, Bluetooth, WiFi"
              />
            </div>
          </div>
        );
      default:
        return (
          <div>
            <label className="block text-sm font-medium text-[#0C4B45] mb-1">Especificaciones Técnicas</label>
            <textarea
              name="specifications.general"
              value={formData.specifications.general || ''}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
              placeholder="Ingrese las especificaciones técnicas del producto..."
            />
          </div>
        );
    }
  };

  const totalImages = existingImages.length + imagePreviews.length;

  // Función para obtener el nombre correcto de la imagen
  const getImageName = (imagePath) => {
    if (!imagePath) return '';
    
    if (imagePath.startsWith('http')) return imagePath;
    
    if (imagePath.startsWith('/uploads/')) {
      return imagePath.substring(9);
    }
    
    if (imagePath.startsWith('uploads/')) {
      return imagePath.substring(8);
    }
    
    return imagePath;
  };

  if (isLoadingProduct) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-[#0C4B45]/5 to-white p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0C4B45]"></div>
        <p className="mt-4 text-xl font-medium text-[#0C4B45]">Cargando producto...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0C4B45]/10 via-white to-[#F2A9FD]/15 p-3 sm:p-6 lg:p-8">
      <div className="w-full max-w-7xl mx-auto bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl ring-1 ring-[#0C4B45]/10 overflow-hidden">
        <div className="p-5 sm:p-8 lg:p-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8 pb-6 border-b border-[#0C4B45]/10">
            <div><p className="text-xs font-bold tracking-[0.18em] text-[#662D8F] uppercase mb-2">Administración de catálogo</p><h2 className="text-2xl md:text-3xl font-bold text-[#0C4B45] flex items-center">
              {mode === 'edit' ? (
                <>
                  <FiEdit className="mr-2" />
                  Editar Producto
                </>
              ) : (
                'Nuevo Producto Tecnológico'
              )}
            </h2><p className="text-sm text-gray-500 mt-2">Completa la información esencial para publicar correctamente.</p></div>
            <button
              onClick={onCancel}
            className="flex items-center px-4 py-2 text-[#0C4B45] hover:bg-[#0C4B45]/10 rounded-xl transition-colors"
            >
              <FiArrowLeft className="mr-2" />
              Volver
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-10">
              {/* Columna Izquierda */}
              <div className="space-y-7 min-w-0">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-[#0C4B45] mb-1">Nombre del Producto *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border ${getFieldError('name') ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#662D8F] focus:border-transparent transition-shadow`}
                    placeholder="Ej: Laptop HP EliteBook 840 G8"
                  />
                  {getFieldError('name') && <p className="mt-1 text-sm text-red-600">{getFieldError('name')}</p>}
                </div>

                {/* Marca y Modelo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0C4B45] mb-1">Marca *</label>
                    <select
                      name="brand"
                      value={formData.brand}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border ${getFieldError('brand') ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#662D8F] focus:border-transparent`}
                    >
                      <option value="">Seleccione una marca</option>
                      {brands.map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                    {getFieldError('brand') && <p className="mt-1 text-sm text-red-600">{getFieldError('brand')}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0C4B45] mb-1">Modelo *</label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border ${getFieldError('model') ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#662D8F] focus:border-transparent`}
                      placeholder="Número de modelo"
                    />
                    {getFieldError('model') && <p className="mt-1 text-sm text-red-600">{getFieldError('model')}</p>}
                  </div>
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-[#0C4B45] mb-1">Descripción *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className={`w-full px-4 py-3 rounded-lg border ${getFieldError('description') ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#662D8F] focus:border-transparent`}
                    placeholder="Describe las características y beneficios del producto..."
                  />
                  {getFieldError('description') && <p className="mt-1 text-sm text-red-600">{getFieldError('description')}</p>}
                </div>

                {/* Especificaciones Técnicas */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-[#0C4B45] mb-3">Especificaciones Técnicas</h3>
                  {renderSpecificationFields()}
                </div>
              </div>

              {/* Columna Derecha */}
              <div className="space-y-7 min-w-0 xl:border-l xl:border-[#0C4B45]/10 xl:pl-10">
                {/* Vista previa de imágenes */}
                <div>
                  <label className="block text-sm font-medium text-[#0C4B45] mb-1">
                    Imágenes del Producto {mode === 'create' ? '*' : ''}
                    <span className="text-xs text-gray-500 ml-1">(Máximo 4, 3MB por imagen)</span>
                  </label>
                  <div className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-4 md:p-6 transition-colors ${
                    errors.images ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }`}>
                    {totalImages > 0 ? (
                      <div className="grid grid-cols-2 gap-3 md:gap-4 w-full">
                        {/* Mostrar imágenes existentes */}
                        {existingImages.map((image, index) => {
                          const imageName = getImageName(image);
                          return (
                            <div key={`existing-${index}`} className="relative group">
                              <img 
                                src={`${ASSET_BASE_URL}/uploads/${imageName}`} 
                                alt={`Imagen existente ${index + 1}`} 
                                className="h-24 md:h-32 w-full object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.src = `${ASSET_BASE_URL}/uploads/${image}`;
                                }}
                              />
                              {mode === 'edit' && (
                                <button
                                  type="button"
                                  onClick={() => removeImage(index, 'existing')}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <FiX size={14} className="md:w-4 md:h-4" />
                                </button>
                              )}
                              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 text-center">
                                Existente {index + 1}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Mostrar vistas previas de nuevas imágenes */}
                        {imagePreviews.map((preview, index) => (
                          <div key={`new-${index}`} className="relative group">
                            <img 
                              src={preview} 
                              alt={`Vista previa ${index + 1}`} 
                              className="h-24 md:h-32 w-full object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index, 'new')}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <FiX size={14} className="md:w-4 md:h-4" />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 text-center">
                              Nueva {index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center">
                        <FiUpload className="mx-auto text-gray-400 text-2xl md:text-3xl mb-2" />
                        <p className="text-sm text-gray-500">Arrastra imágenes o haz clic para seleccionar (máx. 4)</p>
                        <p className="text-xs text-gray-400 mt-1">Las imágenes se comprimirán automáticamente</p>
                      </div>
                    )}
                    <input
                      type="file"
                      onChange={handleImageChange}
                      accept="image/*"
                      className="hidden"
                      id="image-upload"
                      multiple
                      disabled={totalImages >= 4}
                      aria-label="Agregar imágenes del producto"
                    />
                    <label 
                      htmlFor="image-upload"
                      className={`mt-4 px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                        totalImages >= 4 
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                          : 'bg-[#0C4B45]/10 hover:bg-[#0C4B45]/20 text-[#0C4B45]'
                      }`}
                    >
                      {totalImages > 0 ? 'Agregar Más Imágenes' : 'Seleccionar Imágenes'}
                    </label>
                    {totalImages > 0 && (
                      <p className="text-xs text-gray-500 mt-2">
                        {totalImages} de 4 imágenes seleccionadas
                      </p>
                    )}
                    {errors.images && <p className="mt-2 text-sm text-red-600">{errors.images}</p>}
                  </div>
                </div>

                {/* Categoría */}
                <div>
                  <label className="block text-sm font-medium text-[#0C4B45] mb-1">Categoría *</label>
                  <div className="relative">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border ${getFieldError('category') ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#662D8F] focus:border-transparent`}
                    >
                      <option value="">Selecciona una categoría</option>
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                    {formData.category && (
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#0C4B45]">
                        {categoryIcons[formData.category]}
                      </span>
                    )}
                  </div>
                  {getFieldError('category') && <p className="mt-1 text-sm text-red-600">{getFieldError('category')}</p>}
                </div>

                {/* Precio, Stock y Garantía */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#0C4B45] mb-1">Precio *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        className={`w-full pl-8 pr-4 py-3 rounded-lg border ${getFieldError('price') ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#662D8F] focus:border-transparent`}
                        placeholder="0.00"
                      />
                    </div>
                    {getFieldError('price') && <p className="mt-1 text-sm text-red-600">{getFieldError('price')}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0C4B45] mb-1">Stock *</label>
                    <input
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleChange}
                      min="0"
                      className={`w-full px-4 py-3 rounded-lg border ${getFieldError('stock') ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#662D8F] focus:border-transparent`}
                      placeholder="Cantidad"
                    />
                    {getFieldError('stock') && <p className="mt-1 text-sm text-red-600">{getFieldError('stock')}</p>}
                  </div>
                </div>

                {/* Garantía */}
                <div>
                  <label className="block text-sm font-medium text-[#0C4B45] mb-1">Garantía (meses)</label>
                  <select
                    name="warranty"
                    value={formData.warranty}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#662D8F] focus:border-transparent"
                  >
                    <option value="4">4 meses</option>
                    <option value="6">6 meses</option>
                    <option value="12">12 meses</option>
                    <option value="24">24 meses</option>
                    <option value="36">36 meses</option>
                  </select>
                </div>

                {/* Destacado */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                    className="h-5 w-5 text-[#662D8F] rounded focus:ring-[#662D8F]"
                  />
                  <label htmlFor="featured" className="flex items-center text-sm text-[#0C4B45]">
                    <FiStar className="mr-2 text-yellow-500" />
                    Producto Destacado
                  </label>
                </div>
              </div>
            </div>

            {/* Botón de envío */}
            <div className="pt-4 flex flex-col-reverse md:flex-row justify-between gap-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center justify-center px-6 py-3 rounded-lg text-white font-medium transition-all ${
                  loading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-[#662D8F] to-[#512577] hover:from-[#512577] hover:to-[#662D8F] shadow-lg'
                }`}
              >
                {loading ? (
                  <>
                    <FiLoader className="animate-spin mr-2" />
                    {mode === 'edit' ? 'Actualizando...' : 'Creando...'}
                  </>
                ) : (
                  <>
                    <FiSave className="mr-2" />
                    {mode === 'edit' ? 'Actualizar Producto' : 'Crear Producto'}
                  </>
                )}
              </motion.button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
